import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {Button, ListItemText, Menu, MenuItem, Paper, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useState} from "react";
import {useTranslation} from "react-i18next";

/** The usual key of the blank resting state; option keys never take it. */
export const BLANK_TEMPLATE_KEY = 'BLANK';

/** What the picker shows for one template. Whatever stands behind it never reaches this component. */
export type TemplatePickerOption = {
    key: string
    name: string
    description?: string
};

/** The resting state of a picker that may be left empty: what its card says, and the key it reports. */
export type TemplatePickerBlank = {
    key: string
    name: string
    description: string
};

type Props = {
    /** Names the menu button and its menu (`<id>_button`, `<id>_menu`); unique on the page. */
    id: string
    options: TemplatePickerOption[]
    selectedKey: string
    onSelect: (key: string) => void
    disabled?: boolean
    /** Over the card; "Start with" by default. */
    label?: string
    /** The button that opens the menu; "Use a template" by default. */
    menuLabel?: string
    /**
     * The card while no option is picked, and what "Remove template" goes back to. Without it
     * there is nothing to remove: the caller keeps one of the options picked.
     */
    blank?: TemplatePickerBlank
    /** Shows the pick without a way to change it, e.g. the only template there is. */
    readOnly?: boolean
};

/**
 * Picks a template, nothing more: it reports the chosen key and the caller decides what that means.
 * The printouts' and digital signing's create dialogs use it.
 */
function TemplatePicker({id, options, selectedKey, onSelect, disabled, label, menuLabel, blank, readOnly}: Props) {
    const {t} = useTranslation();

    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const menuOpen = !!menuAnchor;
    const buttonId = `${id}_button`;
    const menuId = `${id}_menu`;

    const selected = options.find(option => option.key === selectedKey);
    const shown = selected ?? blank;

    return (
        <Stack gap={1.5}>
            <Typography variant={'subtitle2'} color={'text.secondary'}>
                {label ?? t('Start with')}
            </Typography>

            <Paper variant={'outlined'} sx={{p: 2}}>
                <Stack gap={0.25}>
                    <Typography variant={'body1'} sx={{fontWeight: 500}}>
                        {shown?.name}
                    </Typography>
                    {!!shown?.description && (
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {shown.description}
                        </Typography>
                    )}
                </Stack>

                {!readOnly && (
                    <>
                        <Stack direction={'row'} flexWrap={'wrap'} gap={0.5} sx={{mt: 1.5}}>
                            <Button
                                id={buttonId}
                                variant={'text'}
                                color={'inherit'}
                                endIcon={<ExpandMoreIcon/>}
                                disabled={disabled}
                                aria-haspopup={'menu'}
                                aria-controls={menuOpen ? menuId : undefined}
                                aria-expanded={menuOpen ? 'true' : undefined}
                                onClick={event => setMenuAnchor(event.currentTarget)}
                                sx={{color: 'text.secondary'}}
                            >
                                {menuLabel ?? t('Use a template')}
                            </Button>

                            {!!selected && !!blank && (
                                <Button
                                    variant={'text'}
                                    color={'inherit'}
                                    startIcon={<CloseIcon/>}
                                    disabled={disabled}
                                    onClick={() => onSelect(blank.key)}
                                    sx={{color: 'text.secondary'}}
                                >
                                    {t('Remove template')}
                                </Button>
                            )}
                        </Stack>

                        <Menu
                            id={menuId}
                            anchorEl={menuAnchor}
                            open={menuOpen}
                            onClose={() => setMenuAnchor(null)}
                            MenuListProps={{'aria-labelledby': buttonId}}
                            PaperProps={{sx: {minWidth: 300, maxWidth: 440}}}
                        >
                            {options.length === 0 && (
                                <MenuItem disabled>{t('No templates available')}</MenuItem>
                            )}
                            {options.map(option => (
                                <MenuItem
                                    key={option.key}
                                    selected={option.key === selectedKey}
                                    sx={{whiteSpace: 'normal'}}
                                    onClick={() => {
                                        setMenuAnchor(null);
                                        onSelect(option.key);
                                    }}
                                >
                                    <ListItemText
                                        primary={option.name}
                                        secondary={option.description}
                                    />
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                )}
            </Paper>
        </Stack>
    );
}

export default TemplatePicker;
